import { useEffect, useState } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import * as am4maps from "@amcharts/amcharts4/maps";
import am4geodata_worldIndiaLow from "@amcharts/amcharts4-geodata/worldIndiaLow";
import * as am4charts from "@amcharts/amcharts4/charts";
import styles from "./LanesChart.module.scss";
import LoadingSkeleton from "../../../shared/LoadingSkeleton/index.jsx";
import NoSearchDataComponent from "../../../shared/country-components/no-search-data.js";
import { getLineCoordinates, resetMapLinesAppearance, restoreMapImagesOpacity, updateMapImagesOpacity, updateMapLine } from "@containers/TradeLanesProfile/utils/helpers";

am4core.useTheme(am4themes_animated);
am4core.addLicense("ch-custom-attribution");
// Define marker path
let targetSVG = "M9,0C4.029,0,0,4.029,0,9s4.029,9,9,9s9-4.029,9-9S13.971,0,9,0z M9,15.93 c-3.83,0-6.93-3.1-6.93-6.93S5.17,2.07,9,2.07s6.93,3.1,6.93,6.93S12.83,15.93,9,15.93 M12.5,9c0,1.933-1.567,3.5-3.5,3.5S5.5,10.933,5.5,9S7.067,5.5,9,5.5 S12.5,7.067,12.5,9z";
const LanesChart = ({ mapData = [], loading = false, error = false, imageData = [], homeGeoPoint = { longitude: 5, latitude: 20 }, zoomLevel = 1 }) => {
  const [chartData, setChartData] = useState("");
  useEffect(() => {
    setChartData([...mapData]);
  }, [mapData]);

  useEffect(() => {
    const chart = am4core.create("countrychartdiv", am4maps.MapChart);
    chart.geodata = am4geodata_worldIndiaLow;
    // Create map polygon series
    chart.projection = new am4maps.projections.Miller();
    let polygonSeries = chart.series.push(new am4maps.MapPolygonSeries());
    polygonSeries.useGeodata = true;
    let polygonTemplate = polygonSeries.mapPolygons.template;
    polygonTemplate.fill = am4core.color("#D3D3D3");
    polygonSeries.exclude = ["AQ"];
    polygonSeries.mapPolygons.template.nonScalingStroke = true;

    // Create image series
    let imageSeries = chart.series.push(new am4maps.MapImageSeries());

    // Create a circle image in image series template so it gets replicated to all new images
    let imageSeriesTemplate = imageSeries.mapImages.template;
    let circle = imageSeriesTemplate.createChild(am4core.Circle);
    circle.propertyFields.radius = "radius";
    circle.fill = am4core.color("#ffffff");
    circle.stroke = am4core.color("#FFFFFF");
    circle.strokeWidth = 5;

    // Set property fields
    imageSeriesTemplate.propertyFields.latitude = "latitude";
    imageSeriesTemplate.propertyFields.longitude = "longitude";

    // Zoom control
    chart.zoomControl = new am4maps.ZoomControl();
    chart.chartContainer.wheelable = false;

    let homeButton = new am4core.Button();
    homeButton.events.on("hit", function () {
      chart.goHome();
    });

    homeButton.icon = new am4core.Sprite();
    homeButton.padding(7, 5, 7, 5);
    homeButton.width = 30;
    homeButton.icon.path = "M16,8 L14,8 L14,16 L10,16 L10,10 L6,10 L6,16 L2,16 L2,8 L0,8 L8,0 L16,8 Z M16,8";
    homeButton.marginBottom = 10;
    homeButton.parent = chart.zoomControl;
    homeButton.insertBefore(chart.zoomControl.plusButton);

    // Center on the groups by default
    chart.homeZoomLevel = zoomLevel;
    // Add images
    let imageTemplate = imageSeries.mapImages.template;
    imageTemplate.tooltipText = "{title}";
    imageTemplate.nonScaling = true;

    let marker = imageTemplate.createChild(am4core.Sprite);
    marker.path = targetSVG;
    marker.horizontalCenter = "middle";
    marker.verticalCenter = "middle";
    marker.propertyFields.scale = "scale";
    marker.fill = am4core.color("#7143f1");
    marker.stroke = am4core.color("#fff");

    imageTemplate.adapter.add("tooltipHTML", (text, target) => {
      let data = target.tooltipDataItem.dataContext;
      const { primaryPortName, shipments } = data;
      if (primaryPortName) {
        return '<span className="d-flex align-items-center" style="font-size: 14px;">{primaryPortName} -> {portName} <br/>{shipments} shipments</span>';
      } else if (shipments) {
        return '<span className="d-flex align-items-center" style="font-size: 14px;">{title} <br/>{shipments} shipments</span>';
      } else {
        return text;
      }
    });
    imageSeries.tooltip.getFillFromObject = false;
    imageSeries.tooltip.label.fill = "#000";
    imageSeries.tooltip.background.fill = "#fff";
    imageSeries.tooltip.background.stroke = "#eaecf0";

    imageTemplate.propertyFields.latitude = "latitude";
    imageTemplate.propertyFields.longitude = "longitude";
    imageSeries.data = imageData;
    // Add lines

    let lineSeries = chart.series.push(new am4maps.MapArcSeries());
    lineSeries.mapLines.template.strokeWidth = 1;
    lineSeries.mapLines.template.stroke = am4core.color("#7143f1");
    lineSeries.mapLines.template.shortestDistance = true;
    lineSeries.dataFields.multiGeoLine = "multiGeoLine";

    let lineTemplate = lineSeries.mapLines.template;
    lineTemplate.events.on("over", function (event) {
      lineSeries.mapLines.each(function (mapLines) {
        mapLines.isHover = true;
        mapLines.line.strokeOpacity = 0.1;
        mapLines.arrow.opacity = 0.1;
      });
      event.target.isHover = true;
      event.target.line.strokeOpacity = 1;
      event.target.arrow.opacity = 1;
    });

    lineTemplate.events.on("out", function () {
      lineSeries.mapLines.each(function (mapLines) {
        mapLines.line.strokeOpacity = 0.4;
        mapLines.arrow.opacity = 1;
      });
    });

    //render arrow
    lineTemplate.nonScalingStroke = true;
    lineTemplate.arrow.nonScaling = true;
    lineTemplate.arrow.width = 4;
    lineTemplate.arrow.height = 6;
    lineTemplate.arrow.fill = am4core.color("#7143f1");
    lineTemplate.line.strokeOpacity = 0.4;

    lineTemplate.adapter.add("tooltipHTML", (text, target) => {
      let data = target.tooltipDataItem.dataContext;
      if (target.tooltipDataItem.dataContext && !!data.primaryPortName && !!data.portName) {
        return '<span className="d-flex align-items-center" style="font-size: 14px;">{primaryPortName} -> {portName} <br/>{shipments} shipments</span>';
      } else {
        return text;
      }
    });

    lineSeries.tooltip.getFillFromObject = false;
    lineSeries.tooltip.label.fill = "#000";
    lineSeries.tooltip.background.fill = "#fff";
    lineSeries.tooltip.background.stroke = "#eaecf0";
    lineSeries.data = chartData;
    lineSeries.mapLines.template.line.controlPointDistance = -0.3;
    lineSeries.mapLines.template.line.controlPointPosition = 0.3;

    imageTemplate.events.on("over", function (event) {
      const { latitude, longitude } = event.target || {};
      const [imageLatitudes, imageLongitudes] = getLineCoordinates(lineSeries, latitude, longitude);

      lineSeries.mapLines.each(function (mapLine) {
        const { latitude: lineLatitude, longitude: lineLongitude } = mapLine.multiGeoLine[0][1] || {};
        const { latitude: outLineLatitude, longitude: outLineLongitude } = mapLine.multiGeoLine[0][0] || {};

        let isActive = latitude === lineLatitude && longitude === lineLongitude;
        let outActiveLine = latitude === outLineLatitude && longitude === outLineLongitude;
        if (isActive) {
          updateMapLine(mapLine, isActive);
        } else {
          updateMapLine(mapLine, outActiveLine);
        }
      });

      updateMapImagesOpacity(imageSeries.mapImages, imageLatitudes, imageLongitudes);
      event.target.opacity = 1;
    });

    imageTemplate.events.on("out", function () {
      restoreMapImagesOpacity(imageSeries.mapImages);
      resetMapLinesAppearance(lineSeries.mapLines);
    });

    if (chart.logo) {
      chart.logo.disabled = true;
    }
    chart.cursor = new am4charts.XYCursor();
    return () => {
      if (chart) {
        chart.dispose();
      }
    };
  }, [chartData, imageData, homeGeoPoint]);

  return (
    <>
      <div id="countrychartdiv" className={styles.lanesChartContainer} style={{ width: "100%", display: loading && "none" }} />
      {loading ? <LoadingSkeleton shape={"square"} height={"100%"} width={"100%"} margin={"1rem 1rem 0 0"} /> : error ? <NoSearchDataComponent className={styles.noSearchDataContainer} content={"Unable to fetch the data at current moment."} /> : null}
    </>
  );
};

export default LanesChart;
